import type { NavigateFunction } from "react-router-dom";

import { PATHS } from "@/config/path";

import {
  BRANCH_WIZARD_RETURN_TO_KEY,
  consumeWizardReturnTo,
} from "../wizardReturnTo";
import type { DemoBranchStore } from "./DemoBranchStore";

const STEP_NAMES = [
  "details",
  "business-hours",
  "vibes",
  "amenities",
  "media",
] as const;

export class BranchRouter {
  private stepPaths: string[];
  private maxStep: number = 5;

  constructor(
    private navigate: NavigateFunction,
    private branchStore: DemoBranchStore,
    _initialPathname: string,
    stepPaths: string[]
  ) {
    this.stepPaths = stepPaths;
  }

  setBranchId(branchId: string): void {
    this.stepPaths = [
      PATHS.ADMIN.BRANCH_WIZARD_DETAILS(branchId),
      PATHS.ADMIN.BRANCH_WIZARD_HOURS(branchId),
      PATHS.ADMIN.BRANCH_WIZARD_VIBES(branchId),
      PATHS.ADMIN.BRANCH_WIZARD_AMENITIES(branchId),
      PATHS.ADMIN.BRANCH_WIZARD_MEDIA(branchId),
    ];
  }

  private navigateToStep(step: number) {
    const stepIndex = step - 1;

    if (stepIndex < 0 || stepIndex >= this.stepPaths.length) {
      return;
    }

    const targetPath = this.stepPaths[stepIndex];
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete("returnTo");
    const queryString = searchParams.toString();
    const newPath = queryString ? `${targetPath}?${queryString}` : targetPath;

    this.navigate(newPath);
  }

  toNext() {
    const currentStep = this.getCurrentStep();
    if (currentStep < this.maxStep) {
      this.navigateToStep(currentStep + 1);
    }
  }

  toPrevious() {
    const currentStep = this.getCurrentStep();
    if (currentStep > 1) {
      this.navigateToStep(currentStep - 1);
    } else {
      this.close();
    }
  }

  private getCurrentStepFromPath(pathname: string): number {
    const pathWithoutQuery = pathname.split("?")[0];

    for (let i = 0; i < this.stepPaths.length; i++) {
      if (pathWithoutQuery === this.stepPaths[i]) {
        return i + 1;
      }
    }

    const pathParts = pathWithoutQuery.split("/").filter(Boolean);
    const lastPart = pathParts[pathParts.length - 1];
    const stepIndex = STEP_NAMES.findIndex(name => name === lastPart);
    if (stepIndex >= 0) {
      return stepIndex + 1;
    }

    return 1;
  }

  getCurrentStep(): number {
    return this.getCurrentStepFromPath(window.location.pathname);
  }

  private getFallbackReturnTo(): string {
    const searchParams = new URLSearchParams(window.location.search);
    const brandId =
      this.branchStore.details?.brandId || searchParams.get("brandId") || "";
    const branchId = this.branchStore.details?.id;

    if (brandId) {
      return PATHS.ADMIN.ACCOUNT_BRAND_DETAILS(brandId);
    }
    if (branchId) {
      return PATHS.ADMIN.ACCOUNT_BRANCH(branchId);
    }
    return PATHS.ADMIN.ACCOUNT;
  }

  close() {
    const returnTo =
      consumeWizardReturnTo(BRANCH_WIZARD_RETURN_TO_KEY) ??
      this.getFallbackReturnTo();
    this.navigate(returnTo);
  }
}
